import React, { useEffect, useState } from "react";
import { generateCartEmail, sendCartEmail } from "../api/auth";

export default function CartEmailModal({ open, onClose, cart, clientId, onSent }) {
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [showRawHtml, setShowRawHtml] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [recoveryActionId, setRecoveryActionId] = useState(null);

  // Generate email on open
  useEffect(() => {
    if (open && cart) {
      setSent(false);
      setError("");
      setRecoveryActionId(null);
      handleGenerate();
    }
  }, [open, cart?.id]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    setRecoveryActionId(null);
    try {
      const resp = await generateCartEmail(cart.id, clientId, customInstructions);
      const data = resp?.data || resp;
      setSubject(data.subject || "");
      setBodyHtml(data.body_html || "");
      setRecoveryActionId(data.recovery_action_id || null);
    } catch (err) {
      setError(err?.message || "Failed to generate email");
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!recoveryActionId) {
      setError("No email generated yet. Please generate first.");
      return;
    }
    setSending(true);
    setError("");
    try {
      await sendCartEmail({
        recovery_action_id: recoveryActionId,
        subject,
        body_html: bodyHtml,
      });
      setSent(true);
      if (onSent) onSent(cart.id);
    } catch (err) {
      setError(err?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b dark:border-gray-800 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Send Recovery Email</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              To: {cart?.visitor_email}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Success state */}
          {sent && (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Email Sent!</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Recovery email sent to {cart?.visitor_email}
              </p>
              <button type="button" onClick={onClose}
                className="mt-4 px-4 py-2 bg-[#32B1FF] text-white rounded-lg hover:bg-blue-600 text-sm font-medium">
                Close
              </button>
            </div>
          )}

          {/* Generate / Edit state */}
          {!sent && (
            <>
              {/* Error */}
              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-3">{error}</div>
              )}

              {/* Loading */}
              {generating && (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <svg className="animate-spin h-7 w-7 text-[#32B1FF]" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span className="text-sm text-gray-500 dark:text-gray-400">AI is generating your email...</span>
                  </div>
                </div>
              )}

              {/* Email form */}
              {!generating && (
                <>
                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                      className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none" />
                  </div>

                  {/* Body */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Body</label>
                      <button type="button" onClick={() => setShowRawHtml(!showRawHtml)}
                        className="text-xs text-[#32B1FF] hover:underline">
                        {showRawHtml ? "Preview" : "Edit HTML"}
                      </button>
                    </div>
                    {showRawHtml ? (
                      <textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={12}
                        className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 text-sm font-mono bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none" />
                    ) : (
                      <div className="border dark:border-gray-700 rounded-lg p-4 bg-white max-h-64 overflow-y-auto text-sm text-gray-900"
                        dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                    )}
                  </div>

                  {/* Custom Instructions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Custom Instructions <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea value={customInstructions} onChange={(e) => setCustomInstructions(e.target.value)}
                      rows={2} placeholder="e.g. Use a casual tone, mention our weekend special..."
                      className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none" />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!sent && !generating && (
          <div className="p-5 border-t dark:border-gray-800 flex items-center justify-between shrink-0">
            <button type="button" onClick={handleGenerate} disabled={generating}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Regenerate
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg border dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button type="button" onClick={() => setShowConfirm(true)} disabled={sending || !subject || !bodyHtml}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-[#32B1FF] text-white hover:bg-blue-600 disabled:opacity-50 font-medium">
                {sending ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Send Email?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
              Are you sure you want to send this recovery email to <strong>{cart?.visitor_email}</strong>?
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium">
                Cancel
              </button>
              <button type="button" onClick={() => { setShowConfirm(false); handleSend(); }}
                className="px-4 py-2 text-sm rounded-lg bg-[#32B1FF] text-white hover:bg-blue-600 font-medium">
                Yes, Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
